import React, { useEffect, useMemo, useState } from "react";
import { 
  FiEye, FiEdit2, FiTrash2, FiX, FiPlus, FiDownload, 
  FiSearch, FiChevronRight, FiChevronLeft, FiFilter, FiCalendar 
} from "react-icons/fi";
import { getData, postData, putData, deleteData } from "../../services/FetchNodeservices";
import * as XLSX from "xlsx";
import { useForm } from "react-hook-form";

/* --- HELPERS --- */
const formatDateISO = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date) ? d : date.toISOString().split('T')[0];
};

const displayDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

const StatusBadge = ({ text }) => {
  const isNew = text?.toLowerCase().includes("new");
  const isRevive = text?.toLowerCase().includes("revive");
  let colors = "bg-gray-100 text-gray-600 border border-gray-250";
  if (isNew) colors = "bg-green-50 text-green-700 border border-green-150";
  if (isRevive) colors = "bg-amber-50 text-amber-700 border border-amber-150";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${colors}`}>
      {text || "N/A"}
    </span>
  );
};

export default function BusinessReportTailwind() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  /* --- FILTER STATES --- */
  const [searchTerm, setSearchTerm] = useState("");
  const [verticalFilter, setVerticalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRangeType, setDateRangeType] = useState("all"); // all, today, yesterday, lastWeek, custom
  const [customDates, setCustomDates] = useState({ start: "", end: "" });

  const { register, handleSubmit, reset, setValue } = useForm();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await getData("businessreport/calling_report");
      if (res.status && Array.isArray(res.data)) {
        const formatted = res.data.map((r) => ({
          id: r.id,
          date: r.date,
          client: r.client,
          clientStatus: r.new_revive,
          vertical: r.vertical,
          hrName: r.hr_name,
          hrMobile: r.hr_mobile_number,
          hrEmail: r.hr_email,
          callingStatus: r.calling_status,
          callingFrequency: r.calling_frequency,
          businessProposal: r.business_proposal,
          remark: r.remarks,
          currentCompany: r.currentCompany || "",
          previousCompany: r.previousCompany || "",
          uploadBy: r.user_id,
        }));
        setRows(formatted.sort((a, b) => new Date(b.date) - new Date(a.date)));
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchRows(); }, []);

  /* --- DYNAMIC FILTER OPTIONS --- */
  const uniqueVerticals = useMemo(() => [...new Set(rows.map(r => r.vertical).filter(Boolean))], [rows]);
  const uniqueStatuses = useMemo(() => [...new Set(rows.map(r => r.clientStatus).filter(Boolean))], [rows]);

  /* --- FILTERING LOGIC --- */
  const filtered = useMemo(() => {
    return rows.filter(r => {
      // 1. Global Search
      const matchesSearch = Object.values(r).some(val => 
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      );

      // 2. Dropdown Filters
      const matchesVertical = verticalFilter === "" || r.vertical === verticalFilter;
      const matchesStatus = statusFilter === "" || r.clientStatus === statusFilter;

      // 3. Date Range Logic
      let matchesDate = true;
      if (dateRangeType !== "all") {
        const rowDate = new Date(r.date);
        rowDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateRangeType === "today") {
          matchesDate = rowDate.getTime() === today.getTime();
        } else if (dateRangeType === "yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          matchesDate = rowDate.getTime() === yesterday.getTime();
        } else if (dateRangeType === "lastWeek") {
          const lastWeek = new Date(today);
          lastWeek.setDate(lastWeek.getDate() - 7);
          matchesDate = rowDate >= lastWeek && rowDate <= today;
        } else if (dateRangeType === "custom") {
          const start = customDates.start ? new Date(customDates.start) : null;
          const end = customDates.end ? new Date(customDates.end) : null;
          if (start) start.setHours(0,0,0,0);
          if (end) end.setHours(23,59,59,999);
          
          if (start && end) matchesDate = rowDate >= start && rowDate <= end;
          else if (start) matchesDate = rowDate >= start;
          else if (end) matchesDate = rowDate <= end;
        }
      }

      return matchesSearch && matchesVertical && matchesStatus && matchesDate;
    });
  }, [rows, searchTerm, verticalFilter, statusFilter, dateRangeType, customDates]);

  // Reset pagination when any filter changes
  useEffect(() => { setPage(0); }, [searchTerm, verticalFilter, statusFilter, dateRangeType, customDates]);

  const visibleRows = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  /* --- HANDLERS --- */
  const onSubmit = async (data) => {
    const userId = localStorage.getItem("user_id");
    const payload = {
      ...data,
      new_revive: data.clientStatus,
      remarks: data.remark,
      user_id: userId,
    };

    try {
      const res = editingId 
        ? await putData(`businessreport/update_report/${editingId}`, payload)
        : await postData("businessreport/submit_report", payload);

      if (res.status) {
        alert("Operation Successful");
        setFormOpen(false);
        fetchRows();
      }
    } catch (err) { alert("Action Failed"); }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    reset();
    Object.keys(row).forEach(key => {
      setValue(key, key === 'date' ? formatDateISO(row[key]) : row[key]);
    });
    setFormOpen(true);
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BusinessReport");
    XLSX.writeFile(wb, `Report_${new Date().toLocaleDateString()}.xlsx`);
  };

  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans p-2">
      
      {/* HEADER SECTION */}
      <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Business Report</h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">Live Client Calling Report Dashboard</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button onClick={handleExport} className="flex items-center gap-2 px-5 py-2.5 text-xs font-extrabold text-gray-700 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 transition-all shadow-sm">
            <FiDownload /> Export
          </button>
          <button onClick={() => { setEditingId(null); reset(); setFormOpen(true); }} className="flex items-center gap-2 px-5 py-2.5 text-xs font-extrabold text-white bg-indigo-600 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
            <FiPlus /> New Entry
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="max-w-[1600px] mx-auto bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* Search */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Search Records</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
                <FiSearch className="w-4 h-4" />
              </span>
              <input 
                type="text" 
                placeholder="Search all fields..."
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 text-xs font-semibold transition-all placeholder-gray-450"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Vertical Dropdown */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Vertical</label>
            <select 
              value={verticalFilter}
              onChange={(e) => setVerticalFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 text-xs font-semibold cursor-pointer"
            >
              <option value="">All Verticals</option>
              {uniqueVerticals.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* Client Status Dropdown */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Client Status</label>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 text-xs font-semibold cursor-pointer"
            >
              <option value="">All Client Statuses</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Date Presets */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Date Filter</label>
            <select 
              value={dateRangeType}
              onChange={(e) => setDateRangeType(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 text-xs font-semibold cursor-pointer"
            >
              <option value="all">Any Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="lastWeek">Last 7 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
        </div>
      </div>

      {/* CUSTOM DATE PICKER */}
      {dateRangeType === "custom" && (
        <div className="max-w-[1600px] mx-auto p-5 bg-indigo-50/50 border border-indigo-100/70 rounded-3xl flex flex-wrap gap-6 items-center animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-extrabold text-indigo-700 uppercase tracking-wider ml-1">Date From</span>
            <input 
              type="date" 
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500"
              onChange={(e) => setCustomDates(prev => ({...prev, start: e.target.value}))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-extrabold text-indigo-750 uppercase tracking-wider ml-1">Date To</span>
            <input 
              type="date" 
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500"
              onChange={(e) => setCustomDates(prev => ({...prev, end: e.target.value}))}
            />
          </div>
          <button 
            onClick={() => { setDateRangeType("all"); setCustomDates({start:"", end:""}); }}
            className="mt-6 text-[10px] font-black text-indigo-650 hover:text-indigo-800 uppercase tracking-wider border-b border-indigo-200"
          >
            Clear Custom Range
          </button>
        </div>
      )}

      {/* TABLE DATA */}
      <div className="max-w-[1600px] mx-auto bg-white shadow-xl shadow-indigo-100/10 overflow-hidden border border-gray-100 rounded-3xl">
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh] custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[2000px] text-gray-750">
            <thead className="sticky top-0 z-30 bg-white border-b border-gray-100">
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-extrabold text-gray-400 uppercase tracking-wider text-left">
                {[
                  "Date", "Client", "Client Status", "Vertical", "HR Name", 
                  "Current Company", "Previous Company", "HR Mobile", "HR Email", 
                  "Calling Status", "Freq", "Proposal", "Remark", "Action"
                ].map((h) => (
                  <th key={h} className={`p-4 ${h === 'Action' ? 'sticky right-0 bg-gray-50/50 border-l border-gray-100 shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.03)] text-center' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="14" className="p-20 text-center font-bold text-gray-450 tracking-wider uppercase">Fetching Data...</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan="14" className="p-20 text-center font-bold text-gray-450 tracking-wider uppercase">No Records Found</td></tr>
              ) : visibleRows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/30 transition text-gray-700 group">
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-500">{displayDate(r.date)}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-black text-indigo-950">{r.client}</td>
                  <td className="p-4 whitespace-nowrap"><StatusBadge text={r.clientStatus} /></td>
                  <td className="p-4 whitespace-nowrap">
                    <span className="bg-indigo-50 border border-indigo-100/50 text-indigo-650 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      {r.vertical}
                    </span>
                  </td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-650">{r.hrName}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-650 bg-gray-50/30">{r.currentCompany || "—"}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-650 bg-gray-50/30">{r.previousCompany || "—"}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-500">{r.hrMobile}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-indigo-650 underline decoration-indigo-200 underline-offset-4">{r.hrEmail}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-bold text-gray-700 uppercase">{r.callingStatus}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-500">{r.callingFrequency}</td>
                  <td className="p-4 whitespace-nowrap text-xs font-semibold text-gray-500">{r.businessProposal}</td>
                  <td className="p-4 min-w-[300px] text-xs text-gray-400 italic leading-relaxed">
                    <div className="line-clamp-1 group-hover:line-clamp-none transition-all">{r.remark || "—"}</div>
                  </td>
                  <td className="p-4 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50/30 border-l border-gray-100 shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.03)] transition-colors">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => {setSelectedReport(r); setViewOpen(true);}} className="p-1.5 text-gray-500 hover:text-indigo-600 rounded-lg hover:bg-white border border-gray-100 hover:border-indigo-100 transition-colors" title="View details"><FiEye size={12}/></button>
                      <button onClick={() => handleEdit(r)} className="p-1.5 text-gray-500 hover:text-indigo-650 rounded-lg hover:bg-white border border-gray-100 hover:border-indigo-150 transition-colors" title="Edit entry"><FiEdit2 size={12}/></button>
                      <button onClick={() => { if(window.confirm("Confirm deletion?")) deleteData(`businessreport/calling_report/${r.id}`).then(fetchRows); }} className="p-1.5 text-red-500 hover:text-red-750 rounded-lg hover:bg-red-50 border border-gray-100 hover:border-red-150 transition-colors" title="Delete entry"><FiTrash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider">
            Showing {visibleRows.length} of {filtered.length} Entries • Page {page + 1}
          </span>
          <div className="flex gap-2">
            <button 
              disabled={page === 0} 
              onClick={() => setPage(p => p - 1)} 
              className="p-1.5 bg-white border border-gray-200 rounded-xl disabled:opacity-30 hover:bg-gray-50 text-gray-500 transition-all shadow-sm"
            >
              <FiChevronLeft size={16}/>
            </button>
            <button 
              disabled={(page + 1) * rowsPerPage >= filtered.length} 
              onClick={() => setPage(p => p + 1)} 
              className="p-1.5 bg-white border border-gray-200 rounded-xl disabled:opacity-30 hover:bg-gray-50 text-gray-500 transition-all shadow-sm"
            >
              <FiChevronRight size={16}/>
            </button>
          </div>
        </div>
      </div>

      {/* CREATE/EDIT DRAWER */}
      {formOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/40 backdrop-blur-sm transition-all duration-300">
          <div className="w-full max-w-xl bg-white h-full shadow-2xl p-8 overflow-y-auto animate-slide-in">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-lg font-black text-indigo-950 uppercase tracking-tight">{editingId ? 'Edit Record' : 'Create Record'}</h3>
                <div className="h-1 w-10 bg-indigo-650 mt-1.5 rounded-full"></div>
              </div>
              <button onClick={() => setFormOpen(false)} className="p-2.5 hover:bg-gray-100 rounded-2xl transition-all text-gray-400"><FiX size={20}/></button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pb-10 font-sans">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Date</label>
                  <input type="date" {...register("date")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Client Status</label>
                  <select {...register("clientStatus")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 cursor-pointer">
                    <option value="New">New</option>
                    <option value="Revive">Revive</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Client Name</label>
                <input {...register("client")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200" placeholder="E.g. Apple Inc."/>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Vertical</label>
                  <input {...register("vertical")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">HR POC Name</label>
                  <input {...register("hrName")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Current Company</label>
                  <input {...register("currentCompany")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Previous Company</label>
                  <input {...register("previousCompany")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">HR Mobile</label>
                  <input {...register("hrMobile")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">HR Email</label>
                  <input type="email" {...register("hrEmail")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Calling Status</label>
                  <input {...register("callingStatus")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Freq / Proposal</label>
                  <input {...register("callingFrequency")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200"/>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">Remarks</label>
                <textarea rows="3" {...register("remark")} className="w-full px-4 py-2.5 bg-[#f4f6fa]/50 border border-gray-100 rounded-xl text-xs font-medium focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200" placeholder="Interaction details..."></textarea>
              </div>

              <button type="submit" className="w-full py-3.5 bg-indigo-600 text-white text-xs font-extrabold uppercase tracking-[0.2em] rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                {editingId ? 'Update Record' : 'Save Record'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* VIEW DETAILS MODAL */}
      {viewOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-[2rem] w-full max-w-lg p-8 shadow-2xl relative overflow-hidden border border-gray-100">
            <div className="absolute top-0 left-0 w-full h-2 bg-indigo-600"></div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-base font-black text-indigo-950 tracking-tight uppercase">Data Preview</h3>
              <button onClick={() => setViewOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-all text-slate-400"><FiX size={18}/></button>
            </div>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {Object.entries(selectedReport || {}).map(([k, v]) => (
                <div key={k} className="flex flex-col border-b border-gray-50 pb-2">
                  <span className="text-[9px] font-extrabold text-gray-400 uppercase tracking-wider mb-1">{k.replace(/([A-Z])/g, ' $1')}</span>
                  <span className="text-xs font-semibold text-gray-700 leading-relaxed">{String(v || "—")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      `}} />
    </div>
  );
}